import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { settingService } from "../services";
import { buildParams } from "/app/src/helpers/params";

/**
 * Initializes multiple setting operations including query management and mutations.
 *
 * @param {Object} params - The parameters for multiple settings.
 * @param {string|number} params.integrationId - The integration identifier used to fetch and update settings.
 * @param {Array} params.settingTypes - An array of setting types to be managed.
 * @returns {Object} An object containing query data and a submitHandler for updating settings.
 */
const useMultipleSettings = ({ integrationId, settingTypes }) => {
  const queryClient = useQueryClient();
  const queryKey = ["settings", integrationId, ...settingTypes];

  // Fetches all settings for the given integrationId and settingTypes
  const settingsQuery = useQuery({
    queryKey,
    queryFn: () =>
      settingService.getAll(
        buildParams({ integrationId, type: `[or]${settingTypes.join(";")}` }),
      ),
  });

  const { mutateAsync: updateSettingsMutation } = useMutation({
    mutationFn: async (formValues) => {
      const promises = settingTypes
        .map((type) => {
          const formValueKey = type;
          const newValue = formValues[formValueKey]; // Get the new value from form values
          const existingSetting = settingsQuery.data?.settings.find(
            (setting) => setting.type === type,
          );

          // If the value is the same as the existing one, skip the update
          if (existingSetting && existingSetting.value === newValue) {
            return null;
          }

          if (!existingSetting) {
            return settingService.createSingle({
              name: type,
              value: newValue,
              type,
              integrationId,
            });
          } else {
            return settingService.updateSingle(existingSetting.id, {
              value: newValue,
            });
          }
        })
        // Filters out null values to only include promises for changed settings
        .filter((promise) => promise !== null);

      // Execute all API calls
      return Promise.all(promises).then((results) =>
        // Merge updated settings with unchanged ones
        settingTypes.map((type) => {
          const updatedSetting = results.find(
            (result) => result?.setting?.type === type,
          );
          if (updatedSetting) {
            return updatedSetting.setting;
          }
          return settingsQuery.data?.settings.find(
            (setting) => setting.type === type,
          );
        }),
      );
    },
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, {
        settings: response,
      });
    },
  });

  /**
   * Handles the form submission to update settings.
   *
   * @param {Object} values - The form values containing new settings.
   * @param {Object} actions - The formik actions object containing helper functions.
   * @returns {Promise<void>} A promise that resolves once the settings update is processed.
   */
  const updateSettings = async (values) => {
    await updateSettingsMutation(values);
  };

  // Maps the settings to their respective types
  const settingsMap = settingTypes.reduce((acc, type) => {
    const setting = settingsQuery.data?.settings.find((s) => s.type === type);
    acc[type] = setting?.value;
    return acc;
  }, {});

  return {
    ...settingsQuery,
    ...settingsMap,
    submitHandler: updateSettings,
  };
};

export default useMultipleSettings;
